<template>
  <v-form ref="contractorForm" v-model="isFormValid" lazy-validation>
    <v-tabs v-model="tab">
      <v-tab :key="CONTRACT_DETAILS">
        Contract Details
      </v-tab>
      <v-tab :key="COMMISSIONS">
        Commissions
      </v-tab>
      <v-tab :key="EXPENSES">
        Expenses
      </v-tab>
    </v-tabs>
    <v-tabs-items v-model="tab">
      <v-tab-item :key="CONTRACT_DETAILS">
        <v-row class="mt-3">
          <v-col cols="12" md="6">
            <v-row>
              <v-col cols="12">
                <v-card-title class="px-0 py-0">Contract</v-card-title>
              </v-col>
            </v-row>
            <v-row class="mt-0">
              <v-col cols="12" sm="6">
                <v-text-field
                  outlined
                  dense
                  validate-on-blur
                  label="Job Title"
                  v-model="jobTitle"
                  :rules="[required('Job Title is Required')]"
                  :readonly="readonly"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="6">
                <FormattedNumberField
                  label="Hours Worked Monthly"
                  :value="workedHoursAverage"
                  :required="true"
                  :dense="true"
                  :readonly="readonly"
                  @valueChange="(newValue) => this.workedHoursAverage = newValue"
                ></FormattedNumberField>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12" sm="6">
                <PayFrequenciesDropdownList
                  :isRequired="true"
                  :payFrequency="payFrequencyId"
                  :readonly="readonly"
                  :dense="true"
                  @payFrequencyChangeHandler="(newValue) => this.payFrequencyId = newValue">
                </PayFrequenciesDropdownList>
              </v-col>
              <v-col cols="12" sm="6">
                <ContractTypesDropDownList
                  :id="contractTypeId"
                  :isRequired="true"
                  :readonly="readonly"
                  :dense="true"
                  @change="(newValue) => {
                      this.contractTypeId = newValue;
                      this.lengthOfContract = null;
                      this.conversionFee = null;
                    }"
                ></ContractTypesDropDownList>
              </v-col>
            </v-row>
            <v-row v-if="isContractToHire">
              <v-col cols="12" sm="6">
                <FormattedNumberField
                  label="Length of Contract (Months)"
                  :onlyInt="true"
                  :value="lengthOfContract"
                  :validate-on-blur="true"
                  :readonly="readonly"
                  :dense="true"
                  @valueChange="(newValue) => this.lengthOfContract = newValue"
                ></FormattedNumberField>
              </v-col>
              <v-col cols="12" sm="6">
                <FormattedNumberField
                  label="Conversion Fee"
                  :value="conversionFee"
                  prepend-inner-icon="attach_money"
                  :validate-on-blur="true"
                  :readonly="readonly"
                  :dense="true"
                  @valueChange="(newValue) => this.conversionFee = newValue"
                ></FormattedNumberField>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12" sm="6">
                <SimpleDatePicker
                  label="Start Date"
                  :value="startAt"
                  :isRequired="true"
                  :readonly="readonly"
                  :dense="true"
                  @selected-date-change="(newValue) => this.startAt = newValue"
                ></SimpleDatePicker>
              </v-col>
              <v-col cols="12" sm="6">
                <SimpleDatePicker
                  label="End Date"
                  :value="endAt"
                  :readonly="readonly"
                  :isRequired="isContractToHire"
                  :clearable="true"
                  :dense="true"
                  :min-date="startAt"
                  :min-date-enable="true"
                  @selected-date-change="(newValue) => this.endAt = newValue"
                ></SimpleDatePicker>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12" sm="6">
                <v-radio-group
                  label="Consultant Type"
                  class="mt-0"
                  v-model="employeeTypeId"
                  :readonly="readonly"
                  dense
                  @change="() => {this.corporateName = null; this.ein = null;}"
                >
                  <v-radio class="my-0" label="1099" :value="INDEPENDENT_CONTRACTOR"></v-radio>
                  <v-radio class="my-0" label="C2C" :value="CORP_TO_CORP"></v-radio>
                  <v-radio class="my-0" label="W2 no Benefits" :value="EMPLOYEE_NO_BENEFITS"></v-radio>
                  <v-radio class="my-0" label="W2 + Benefits" :value="EMPLOYEE_WITH_BENEFITS"></v-radio>
                </v-radio-group>
              </v-col>
              <v-col cols="12" sm="6" v-if="isC2C">
                <v-row>
                  <v-col cols="12" class="pt-5">
                    <v-text-field
                      label="Corp. Name"
                      v-model="corporateName"
                      validate-on-blur
                      :readonly="readonly"
                      outlined
                      dense
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" class="py-0">
                    <v-text-field
                      label="EIN"
                      v-model="ein"
                      validate-on-blur
                      :readonly="readonly"
                      outlined
                      dense
                      :maxlength="10"
                    ></v-text-field>
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12" sm="6">
                <TimeSheetTypesDropdown
                  label="Time Sheet Type"
                  :value="timeSheetType"
                  :isRequired="true"
                  :readonly="readonly"
                  :dense="true"
                  @typeChange="(newValue) => this.timeSheetType = newValue"
                ></TimeSheetTypesDropdown>
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-if="this.timeSheetType === 3"
                  label="Clock In/Clock Out IP Address"
                  v-model="clockIPAddress"
                  dense
                  outlined
                ></v-text-field>
              </v-col>
            </v-row>
          </v-col>
          <v-divider vertical></v-divider>
          <v-col cols="12" md="6">
            <v-row>
              <v-col cols="12" sm="6">
                <v-row>
                  <v-col cols="12" class="px-3">
                    <v-card-title class="px-0 py-0">
                      Pay Details
                    </v-card-title>
                  </v-col>
                </v-row>
                <v-row class="mt-0">
                  <v-col cols="12">
                    <FormattedNumberField
                      label="Pay Rate"
                      :value="payRate"
                      prepend-inner-icon="attach_money"
                      :dense="true"
                      :required="true"
                      :readonly="readonly"
                      @valueChange="(newValue) => this.payRate = newValue"
                      @blur="setPayRateType"
                    ></FormattedNumberField>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="12">
                    <PayRateTypesDropdownList
                      :isRequired="true"
                      :payRateType="payRateTypeId"
                      :readonly="readonly"
                      :dense="true"
                      @payRateTypeChangeHandler="payRateTypeChangeHandler"
                    >
                    </PayRateTypesDropdownList>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="12">
                    <FormattedNumberField
                      label="Monthly Total Pay Rate"
                      :value="totalPayRate"
                      :readonly="true"
                      :hint="totalPayRateHint"
                      :persistent-hint="!!totalPayRateHint"
                      :dense="true"
                      prepend-inner-icon="attach_money"
                    ></FormattedNumberField>
                  </v-col>
                </v-row>
              </v-col>
              <v-divider vertical></v-divider>
              <v-col cols="12" sm="6">
                <v-row>
                  <v-col cols="12" class="px-3">
                    <v-card-title class="px-0 py-0">
                      Bill Details
                    </v-card-title>
                  </v-col>
                </v-row>
                <v-row class="mt-0">
                  <v-col cols="12">
                    <FormattedNumberField
                      label="Bill Rate"
                      :value="billRate"
                      :required="true"
                      :readonly="readonly"
                      prepend-inner-icon="attach_money"
                      :dense="true"
                      @valueChange="(newValue) => this.billRate = newValue"
                      @blur="setBillRateType"
                    ></FormattedNumberField>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="12">
                    <BillRateTypesDropdownList
                      :isRequired="true"
                      :billRateType="billRateTypeId"
                      :readonly="readonly"
                      :dense="true"
                      @billRateTypeChangeHandler="(newValue) => this.billRateTypeId = newValue">
                    </BillRateTypesDropdownList>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="12">
                    <FormattedNumberField
                      label="Monthly Net Bill Rate"
                      :value="netBillRate"
                      :readonly="true"
                      :hint="netBillRateHint"
                      :persistent-hint="!!netBillRateHint"
                      :dense="true"
                      prepend-inner-icon="attach_money"
                    ></FormattedNumberField>
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="6" class="pb-0">
                <FormattedNumberField
                  label="Markup %"
                  :value="markupPercentage"
                  :readonly="true"
                  :dense="true"
                  appendIcon="percent"
                  :hideDetails="true"
                ></FormattedNumberField>
              </v-col>
              <v-col cols="6" class="pb-0">
                <FormattedNumberField
                  label="Monthly Gross Profit"
                  :value="grossProfit"
                  :readonly="true"
                  :hint="grossProfitHint"
                  :persistent-hint="!!grossProfitHint"
                  :dense="true"
                  prepend-inner-icon="attach_money"
                ></FormattedNumberField>
              </v-col>
            </v-row>
            <v-row>
              <v-col
                class="pt-0"
                cols="12"
                sm="4"
              >
                <v-checkbox
                  v-if="isHourlyPaid"
                  label="Calculate Automatic OT"
                  v-model="overtimeEligible"
                  :readonly="readonly"
                  dense
                ></v-checkbox>
              </v-col>
              <v-col cols="12" sm="4">
                <FormattedNumberField
                  v-if="overtimeEligible"
                  label="Week Threshold (Hours)"
                  :value="overtimeThreshold"
                  :dense="true"
                  :required="overtimeEligible"
                  :readonly="readonly"
                  @valueChange="(newValue) => this.overtimeThreshold = newValue"
                ></FormattedNumberField>
              </v-col>
              <v-col cols="12" sm="4">
                <FormattedNumberField
                  v-if="overtimeEligible"
                  label="Rate"
                  :value="overtimeRate"
                  :dense="true"
                  :required="overtimeEligible"
                  :readonly="readonly"
                  prepend-inner-icon="attach_money"
                  @valueChange="(newValue) => this.overtimeRate = newValue"
                ></FormattedNumberField>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12" md="6">
                <v-checkbox
                  label="Contractor Commission Enabled"
                  v-model="contractorCommissionEnabled"
                  :readonly="readonly || !canSetContractorCommission"
                  dense
                ></v-checkbox>
              </v-col>
              <v-col cols="12" md="6">
                <FormattedNumberField
                  v-if="contractorCommissionEnabled"
                  label="Markup %"
                  appendIcon="percent"
                  :value="contractorCommissionMarkup"
                  :readonly="readonly || !canSetContractorCommission"
                  :dense="true"
                  @valueChange="(newValue) => this.contractorCommissionMarkup = newValue"
                ></FormattedNumberField>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12">
                <v-btn
                  block
                  outlined
                  color="primary"
                  @click="openMarkupCalculator"
                >
                  Markup Calc.
                </v-btn>
              </v-col>
            </v-row>
          </v-col>
        </v-row>
      </v-tab-item>
      <v-tab-item :key="COMMISSIONS">
        <v-row class="my-5">
          <v-col cols="12" class="px-3">
            <CommissionsCollector
              ref="commissionCollector"
              commissionComponent="CommissionInput"
              commissionType="commission"
              :commissions="commissions"
              :readonly="readonly"
              class="mb-2"
              @commissionsChange="handleCommissionsChange"
            ></CommissionsCollector>
          </v-col>
        </v-row>
      </v-tab-item>
      <v-tab-item :key="EXPENSES">
        <v-row>
          <v-col cols="12">
            <ExpensesCard
              class="my-3"
              cardTitle="Expenses"
              :expensesList="expenses"
              :readonly="readonly"
              :showActive="false"
              :showExpenseAmountType="true"
              @expensesListChange="(newValue) => this.expenses = newValue"
            ></ExpensesCard>
          </v-col>
        </v-row>
      </v-tab-item>
    </v-tabs-items>
    <MarkupCalculator
      ref="markupCalculator"
    ></MarkupCalculator>
  </v-form>
</template>

<script>
import { PayFrequenciesDropdownList } from '@/components/payFrequencies'
import { ContractTypesDropDownList } from '@/components/contractTypes'
import { SimpleDatePicker } from '@/components/shared/datePickers'
import { PayRateTypesDropdownList } from '@/components/payRateTypes'
import { BillRateTypesDropdownList } from '@/components/billRateTypes'
import { CommissionsCollector } from '@/components/commissions'
import { ExpensesCard } from '@/components/expenses'
import { PRE_GP } from '@/lib/constants/expensesTypes'
import {
  CONTRACT_TO_HIRE
} from '@/lib/constants/contractTypeDefault'
import { mapFields } from '@/vueHelpers/vuex'
import {
  EMPLOYEE_WITH_BENEFITS,
  EMPLOYEE_NO_BENEFITS,
  CORP_TO_CORP,
  INDEPENDENT_CONTRACTOR
} from '@/lib/constants/employeeTypes'
import { CREATE_FROM_ZOHO } from '@/lib/constants/dealCreationTypes'
import { getTotalCommisions } from '@/lib/commissions'
import { FormattedNumberField } from '@/components/shared/textFields'
import { MarkupCalculator } from '@/components/assignments'
import {
  getOverheadPercent,
  getMonthlyTotalPayRate,
  getMonthlyNetBillRate,
  getMarkup,
  getExpensesDeduction
} from '@/lib/grossProfitCalculator'
import { formatCurrency, formatPercent, getEmployeeTypeLabel } from '@/lib/display'
import { HOURLY, MONTHLY, ANNUAL } from '@/lib/constants/rateTypes'
import { TimeSheetTypesDropdown } from '@/components/shared/dropdowns'
import { ADMIN, MANAGER, SALES_PERSON } from '@/lib/constants/securityRoles'
import { getCurrentUser, userHasRoles } from '@/lib/authentication'
import { required } from '@/lib/formValidations'

export default {
  props: [
    'readonly',
    'candidateId',
    'workedHoursAverageDefault',
    'overtimeThresholdDefault',
    'overtimeRateDefault'
  ],
  components: {
    PayFrequenciesDropdownList,
    ContractTypesDropDownList,
    SimpleDatePicker,
    PayRateTypesDropdownList,
    BillRateTypesDropdownList,
    CommissionsCollector,
    ExpensesCard,
    FormattedNumberField,
    MarkupCalculator,
    TimeSheetTypesDropdown
  },
  data () {
    return {
      HOURLY,
      MONTHLY,
      ANNUAL,
      isFormValid: null,
      EMPLOYEE_WITH_BENEFITS,
      EMPLOYEE_NO_BENEFITS,
      INDEPENDENT_CONTRACTOR,
      CORP_TO_CORP,
      CREATE_FROM_ZOHO,
      CONTRACT_TO_HIRE,
      PRE_GP,
      tab: 0,
      CONTRACT_DETAILS: 0,
      COMMISSIONS: 1,
      EXPENSES: 2,
      monthlyExpensesDeduction: 0
    }
  },
  computed: {
    ...mapFields('dealV2', ['deal']),
    ...mapFields('settings', [
      'payRateSettings'
    ]),

    currentContract () {
      if (
        !this.deal ||
        !this.deal.candidates ||
        !Array.isArray(this.deal.candidates)
      ) {
        return null
      }

      const candidate = this.deal.candidates.find(
        c => c.id === this.candidateId
      )

      return candidate ? candidate.dealAssignment : null
    },

    jobTitle: {
      get () {
        return this.currentContract
          ? this.currentContract.jobTitle
          : ''
      },
      set (value) {
        this.$emit('setJobTitle', value, this.candidateId)
      }
    },

    workedHoursAverage: {
      get () {
        return this.currentContract
          ? this.currentContract.workedHoursAverage
          : null
      },
      set (value) {
        this.$emit('setWorkedHoursAverage', value, this.candidateId)
      }
    },

    payFrequencyId: {
      get () {
        return this.currentContract
          ? this.currentContract.payFrequencyId
          : null
      },
      set (value) {
        this.$emit('setPayFrequencyId', value, this.candidateId)
      }
    },

    contractTypeId: {
      get () {
        return this.currentContract
          ? this.currentContract.contractTypeId
          : null
      },
      set (value) {
        this.$emit('setContractTypeId', value, this.candidateId)
      }
    },

    lengthOfContract: {
      get () {
        return this.currentContract
          ? this.currentContract.lengthOfContract
          : null
      },
      set (value) {
        this.$emit('setLengthOfContract', value, this.candidateId)
      }
    },

    conversionFee: {
      get () {
        return this.currentContract
          ? this.currentContract.conversionFee
          : null
      },
      set (value) {
        this.$emit('setConversionFee', value, this.candidateId)
      }
    },

    startAt: {
      get () {
        return this.currentContract
          ? this.currentContract.startAt
          : null
      },
      set (value) {
        this.$emit('setStartAt', value, this.candidateId)
      }
    },

    endAt: {
      get () {
        return this.currentContract
          ? this.currentContract.endAt
          : null
      },
      set (value) {
        this.$emit('setEndAt', value, this.candidateId)
      }
    },

    employeeTypeId: {
      get () {
        return this.currentContract
          ? this.currentContract.employeeTypeId
          : null
      },
      set (value) {
        this.$emit('setEmployeeTypeId', value, this.candidateId)
      }
    },

    corporateName: {
      get () {
        return this.currentContract
          ? this.currentContract.corporateName
          : ''
      },
      set (value) {
        this.$emit('setCorporateName', value, this.candidateId)
      }
    },

    ein: {
      get () {
        return this.currentContract
          ? this.currentContract.ein
          : ''
      },
      set (value) {
        this.$emit('setEin', value, this.candidateId)
      }
    },

    payRate: {
      get () {
        return this.currentContract
          ? this.currentContract.payRate
          : null
      },
      set (value) {
        this.$emit('setPayRate', value, this.candidateId)
      }
    },

    payRateTypeId: {
      get () {
        return this.currentContract
          ? this.currentContract.payRateTypeId
          : null
      },
      set (value) {
        this.$emit('setPayRateTypeId', value, this.candidateId)
      }
    },

    billRate: {
      get () {
        return this.currentContract
          ? this.currentContract.billRate
          : null
      },
      set (value) {
        this.$emit('setBillRate', value, this.candidateId)
      }
    },

    billRateTypeId: {
      get () {
        return this.currentContract
          ? this.currentContract.billRateTypeId
          : null
      },
      set (value) {
        this.$emit('setBillRateTypeId', value, this.candidateId)
      }
    },

    overtimeEligible: {
      get () {
        return this.currentContract
          ? this.currentContract.overtimeEligible
          : false
      },
      set (value) {
        this.$emit('setOvertimeEligible', value, this.candidateId)
      }
    },

    overtimeThreshold: {
      get () {
        return this.currentContract
          ? this.currentContract.overtimeThreshold
          : null
      },
      set (value) {
        this.$emit('setOvertimeThreshold', value, this.candidateId)
      }
    },

    overtimeRate: {
      get () {
        return this.currentContract
          ? this.currentContract.overtimeRate
          : null
      },
      set (value) {
        this.$emit('setOvertimeRate', value, this.candidateId)
      }
    },

    timeSheetType: {
      get () {
        return this.currentContract
          ? this.currentContract.timeSheetType
          : null
      },
      set (value) {
        this.$emit('setTimeSheetType', value, this.candidateId)
      }
    },

    clockIPAddress: {
      get () {
        return this.currentContract
          ? this.currentContract.clockIPAddress
          : ''
      },
      set (value) {
        this.$emit('setClockIPAddress', value, this.candidateId)
      }
    },

    commissions: {
      get () {
        return this.currentContract
          ? this.currentContract.commissions
          : []
      },
      set (value) {
        this.$emit('setCommissions', value, this.candidateId)
      }
    },

    expenses: {
      get () {
        return this.currentContract
          ? this.currentContract.expenses
          : []
      },
      set (value) {
        this.$emit('setExpenses', value, this.candidateId)
      }
    },

    contractorCommissionEnabled: {
      get () {
        return this.currentContract
          ? this.currentContract.contractorCommissionEnabled
          : false
      },
      set (value) {
        this.$emit('setContractorCommissionEnabled', value, this.candidateId)
      }
    },

    contractorCommissionMarkup: {
      get () {
        return this.currentContract
          ? this.currentContract.contractorCommissionMarkup
          : null
      },
      set (value) {
        this.$emit('setContractorCommissionMarkup', value, this.candidateId)
      }
    },

    isContractToHire () {
      if (!this.currentContract) return false
      return this.currentContract.contractTypeId === this.CONTRACT_TO_HIRE
    },

    isC2C () {
      if (!this.currentContract) return false
      return this.currentContract.employeeTypeId === this.CORP_TO_CORP
    },

    isHourlyPaid () {
      if (!this.currentContract) return false
      return this.currentContract.payRateTypeId === this.HOURLY
    },

    totalPayRate () {
      const contract = this.currentContract
      if (contract && contract.payRate !== null && contract.payRateTypeId && contract.employeeTypeId && contract.workedHoursAverage !== null) {
        return getMonthlyTotalPayRate(
          getOverheadPercent(contract.employeeTypeId, this.payRateSettings),
          contract.payRate,
          contract.payRateTypeId,
          contract.workedHoursAverage
        )
      }
      return null
    },
    vmsFee () {
      return this.deal.client.vmsFee || 0
    },
    netBillRate () {
      const contract = this.currentContract
      if (contract && this.vmsFee !== null && contract.billRate !== null && contract.billRateTypeId) {
        return getMonthlyNetBillRate(
          this.vmsFee,
          contract.billRate,
          contract.billRateTypeId,
          contract.workedHoursAverage
        )
      }
      return null
    },
    grossProfit () {
      if (this.netBillRate && this.totalPayRate) {
        return this.netBillRate - this.totalPayRate - this.monthlyExpensesDeduction
      }
      return null
    },
    totalPayRateHint () {
      if (this.totalPayRate) {
        return `Total Pay Rate = Pay Rate + ${getEmployeeTypeLabel(this.employeeTypeId)} Overhead (${formatPercent(getOverheadPercent(this.employeeTypeId, this.payRateSettings))})`
      }
      return 'Total Pay Rate = Pay Rate + Overhead'
    },
    netBillRateHint () {
      if (this.netBillRate) {
        return `Net Bill Rate = Bill Rate - VMS Fee (${formatPercent(this.vmsFee)})`
      }
      return 'Net Bill Rate = Bill Rate - VMS Fee'
    },
    grossProfitHint () {
      let hint = 'Gross Profit = Net Bill Rate - Total Pay Rate'
      if (this.monthlyExpensesDeduction > 0) {
        hint += ` - Monthly Expenses (${formatCurrency(this.monthlyExpensesDeduction)})`
      }
      return hint
    },
    markupPercentage () {
      if (this.totalPayRate && this.grossProfit) {
        return getMarkup(this.totalPayRate, this.grossProfit)
      }
      return null
    },
    canSetContractorCommission () {
      return this.userHasRoles({ user: this.getCurrentUser(), allowedRoles: [ADMIN, MANAGER, SALES_PERSON] })
    }
  },
  watch: {
    candidateId () {
      this.$nextTick(() => {
        if (this.$refs.contractorForm) {
          // This avoids showing the validation error to the user
          // when using the navigation buttons to navigate between different candidates in the same deal
          this.$refs.contractorForm.resetValidation()
        }
      })
    },
    contractorExpenses () {
      // First delete the default expenses
      if (this.expenses && this.expenses.length > 0) {
        // Look for the client's default expenses
        let defaultExpenses = this.expenses.filter(expense => !!expense.clientDefaultExpense)
        for (let defaultExpense of defaultExpenses) {
          let indexToDelete = this.expenses.findIndex(expense => expense.id === defaultExpense.id && !!expense.clientDefaultExpense)
          this.expenses.splice(indexToDelete, 1)
        }
      }
      // Then include the default expenses of the existing client chosen
      if (this.contractorExpenses && this.contractorExpenses.length > 0) {
        for (let index = this.contractorExpenses.length - 1; index >= 0; index--) {
          this.expenses.unshift({ ...this.contractorExpenses[index], clientDefaultExpense: true })
        }
      }
    },
    expenses () {
      this.monthlyExpensesDeduction = getExpensesDeduction(this.expenses, this.MONTHLY, this.workedHoursAverage)
    },
    workedHoursAverageDefault () {
      // Whenever the workedHoursAverageDefault from the application settings
      // is retrieved from the server, update the contract with the default value
      // if the current value is null (not set by the user)
      if (this.workedHoursAverage === null ||
          this.workedHoursAverage === undefined ||
          this.workedHoursAverage === 0) {
        this.workedHoursAverage = this.workedHoursAverageDefault
      }
    },
    overtimeThresholdDefault () {
      // Whenever the overtimeThresholdDefault from the application settings
      // is retrieved from the server, update the contract with the default value
      // if the current value is null (not set by the user)
      if (this.overtimeThreshold === null ||
          this.overtimeThreshold === undefined ||
          this.overtimeThreshold === 0) {
        this.overtimeThreshold = this.overtimeThresholdDefault
      }
    },
    overtimeRateDefault () {
      // Whenever the overtimeRateDefault from the application settings
      // is retrieved from the server, update the contract with the default value
      // if the current value is null (not set by the user)
      if (this.overtimeRate === null ||
          this.overtimeRate === undefined ||
          this.overtimeRate === 0) {
        this.overtimeRate = this.overtimeRateDefault
      }
    }
  },
  methods: {
    getTotalCommisions,
    getCurrentUser,
    userHasRoles,
    required,
    handleCommissionsChange (value) {
      this.commissions = value
    },
    setPayRateType () {
      if (this.payRate && !this.payRateTypeId) {
        if (this.payRate < 500) {
          this.payRateTypeId = HOURLY
        } else if (this.payRate < 19999) {
          this.payRateTypeId = MONTHLY
        } else if (this.payRate >= 20000) {
          this.payRateTypeId = ANNUAL
        }
      }
    },
    setBillRateType () {
      if (this.billRate && !this.billRateTypeId) {
        if (this.billRate < 500) {
          this.billRateTypeId = HOURLY
        } else if (this.billRate < 19999) {
          this.billRateTypeId = MONTHLY
        } else if (this.billRate >= 20000) {
          this.billRateTypeId = ANNUAL
        }
      }
    },
    payRateTypeChangeHandler (newValue) {
      if (newValue === MONTHLY || newValue === ANNUAL) {
        this.overtimeEligible = false
      }
      this.payRateTypeId = newValue
    },
    openMarkupCalculator () {
      this.$refs.markupCalculator.showModal()
    },
    isContractValid () {
      return this.$refs.contractorForm.validate() ? true : 'Please fill out the required fields to proceed'
    }
  }
}
</script>
